import { z } from 'zod';
import type {
  AdminExpertDetail,
  ExpertDetailsGroup,
  UpdateExpertDetailsRequest,
} from '@shopexperts/legacy-api';

export type DetailsSectionKey = 'listing' | 'services' | 'reach' | 'account' | 'contact' | 'links';

export type DetailsFieldValue = string | string[];

type DetailsFieldKind = 'text' | 'number' | 'list' | 'idList';

export interface DetailsFieldDef {
  /** Flat form field name; dots are avoided because vee-validate treats them as paths. */
  name: string;
  group: ExpertDetailsGroup;
  /** Key inside the API group. */
  key: string;
  kind: DetailsFieldKind;
  rule: z.ZodTypeAny;
  /**
   * Current value from the detail endpoint. `undefined` means the endpoint does
   * not expose the value, so the field is left out of the form rather than
   * shown empty and risk an admin blanking data they cannot see.
   */
  read: (expert: AdminExpertDetail) => string | number | string[] | null | undefined;
}

const URL_PATTERN = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/\S*)?$/;
const E164_PATTERN = /^\+[1-9]\d{1,14}$/;
const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;

const text = z.string();
const requiredText = (label: string) => z.string().trim().min(1, `${label} is required`);
const requiredEmail = z.string().trim().min(1, 'Email is required').email('Enter a valid email address');
const optionalEmail = z.string().trim().refine(
  (value) => value === '' || z.string().email().safeParse(value).success,
  'Enter a valid email address',
);
const optionalUrl = z.string().trim().refine(
  (value) => value === '' || URL_PATTERN.test(value),
  'Enter a valid URL like www.example.com',
);
const absoluteUrl = z.string().trim().refine(
  (value) => value === '' || z.string().url().safeParse(value).success,
  'Enter a full URL starting with https://',
);
const phone = z.string().trim().refine(
  (value) => value === '' || E164_PATTERN.test(value),
  'Include the country code, e.g. +14165550100',
);
const amount = z.string().trim().refine(
  (value) => value === '' || AMOUNT_PATTERN.test(value),
  'Enter an amount like 450 or 450.00',
);
const list = z.array(z.string());
const limitOverride = z.string().trim().refine(
  (value) => value === '' || (/^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= 100),
  'Enter a whole number from 1 to 100, or leave it empty',
);
const cityId = z.string().refine((value) => value === '' || /^\d+$/.test(value), 'Select a city from the list');

type SocialLinkKey = keyof NonNullable<AdminExpertDetail['social_links']>;

function readSocialLink(expert: AdminExpertDetail, key: SocialLinkKey) {
  if (!expert.social_links) return null;
  const value = expert.social_links[key];
  return value === undefined ? undefined : value;
}

function socialLink(name: string, key: SocialLinkKey, rule: z.ZodTypeAny = optionalUrl): DetailsFieldDef {
  return { name, group: 'social_links', key, kind: 'text', rule, read: (expert) => readSocialLink(expert, key) };
}

const FREELANCER_LISTING_FIELDS: DetailsFieldDef[] = [
  {
    name: 'firstName', group: 'freelancer', key: 'first_name', kind: 'text', rule: requiredText('First name'),
    read: (expert) => (expert.freelancer === undefined ? undefined : expert.freelancer?.first_name ?? null),
  },
  {
    name: 'lastName', group: 'freelancer', key: 'last_name', kind: 'text', rule: requiredText('Last name'),
    read: (expert) => (expert.freelancer === undefined ? undefined : expert.freelancer?.last_name ?? null),
  },
  {
    name: 'role', group: 'freelancer', key: 'role', kind: 'text', rule: text,
    read: (expert) => (expert.freelancer === undefined ? undefined : expert.freelancer?.role ?? null),
  },
];

const AGENCY_LISTING_FIELDS: DetailsFieldDef[] = [
  {
    name: 'agencyName', group: 'agency', key: 'agency_name', kind: 'text', rule: requiredText('Agency name'),
    // The list item's display_name is the agency name verbatim, so it is a safe fallback.
    read: (expert) => (expert.agency === undefined ? expert.display_name : expert.agency?.agency_name ?? null),
  },
  {
    name: 'representativeName', group: 'agency', key: 'representative_name', kind: 'text', rule: text,
    read: (expert) => (expert.agency === undefined ? undefined : expert.agency?.representative_name ?? null),
  },
  {
    name: 'representativeRole', group: 'agency', key: 'representative_role', kind: 'text', rule: text,
    read: (expert) => (expert.agency === undefined ? undefined : expert.agency?.representative_role ?? null),
  },
];

const COMMON_LISTING_FIELDS: DetailsFieldDef[] = [
  { name: 'bio', group: 'expert', key: 'bio', kind: 'text', rule: text, read: (expert) => expert.bio },
  {
    name: 'startingPrice', group: 'expert', key: 'starting_price', kind: 'number', rule: amount,
    read: (expert) => expert.starting_price,
  },
];

const SERVICE_FIELDS: DetailsFieldDef[] = [
  {
    name: 'subcategoryLimitOverride', group: 'offered_services', key: 'subcategory_limit_override', kind: 'number', rule: limitOverride,
    read: (expert) => expert.subcategory_limit_override,
  },
  {
    name: 'subcategories', group: 'offered_services', key: 'subcategory_ids', kind: 'idList', rule: list,
    // Legacy listings can hold several rows for one category with overlapping
    // subcategories, and the API rejects repeated ids.
    read: (expert) => [
      ...new Set(expert.offered_services.flatMap((service) => service.sub_categories.map((sub) => String(sub.id)))),
    ],
  },
];

const REACH_FIELDS: DetailsFieldDef[] = [
  { name: 'country', group: 'expert', key: 'country', kind: 'text', rule: text, read: (expert) => expert.country },
  {
    name: 'cityId', group: 'expert', key: 'city_id', kind: 'number', rule: cityId,
    read: (expert) => (expert.city === undefined ? undefined : expert.city?.id ?? null),
  },
  { name: 'timezone', group: 'expert', key: 'timezone', kind: 'text', rule: text, read: (expert) => expert.timezone },
  { name: 'languages', group: 'expert', key: 'languages', kind: 'list', rule: list, read: (expert) => expert.languages ?? [] },
  { name: 'geoRegions', group: 'expert', key: 'geo_regions', kind: 'list', rule: list, read: (expert) => expert.geo_regions ?? [] },
];

const ACCOUNT_FIELDS: DetailsFieldDef[] = [
  {
    name: 'accountFirstName', group: 'user', key: 'first_name', kind: 'text', rule: text,
    read: (expert) => (expert.user === undefined ? undefined : expert.user?.first_name ?? null),
  },
  {
    name: 'accountLastName', group: 'user', key: 'last_name', kind: 'text', rule: text,
    read: (expert) => (expert.user === undefined ? undefined : expert.user?.last_name ?? null),
  },
  {
    name: 'accountEmail', group: 'user', key: 'email', kind: 'text', rule: requiredEmail,
    // Without the user group, the detail `email` is still the login email whenever a user is linked.
    read: (expert) => (expert.user ? expert.user.email : hasLinkedUser(expert) ? expert.email : undefined),
  },
];

const CONTACT_FIELDS: DetailsFieldDef[] = [
  { name: 'phone', group: 'expert', key: 'phone_no', kind: 'text', rule: phone, read: (expert) => expert.phone_no },
  socialLink('contactEmail', 'contact_email', optionalEmail),
];

const LINK_FIELDS: DetailsFieldDef[] = [
  socialLink('website', 'website'),
  socialLink('shopifyDirectoryLink', 'shopify_directory_link'),
  socialLink('linkedin', 'linkedin_url'),
  socialLink('github', 'github_url'),
  socialLink('x', 'x_url'),
  socialLink('fiverr', 'fiverr_url'),
  socialLink('upwork', 'upwork_url'),
  socialLink('toptal', 'toptal_url'),
  socialLink('storetasker', 'storetasker_url'),
  socialLink('otherListing', 'other_listing_url'),
  socialLink('bookingCallUrl', 'booking_call_url', absoluteUrl),
  socialLink('googleMerchant', 'google_merchant'),
];

export function hasLinkedUser(expert: AdminExpertDetail): boolean {
  return !!expert.user || expert.user_id > 0;
}

function allSectionFields(section: DetailsSectionKey, expert: AdminExpertDetail): DetailsFieldDef[] {
  switch (section) {
    case 'listing':
      return [
        ...(expert.expert_type === 'agency' ? AGENCY_LISTING_FIELDS : FREELANCER_LISTING_FIELDS),
        ...COMMON_LISTING_FIELDS,
      ];
    case 'services':
      return SERVICE_FIELDS;
    case 'reach':
      return REACH_FIELDS;
    case 'account':
      return hasLinkedUser(expert) ? ACCOUNT_FIELDS : [];
    case 'contact':
      return CONTACT_FIELDS;
    case 'links':
      return LINK_FIELDS;
  }
}

/** Fields the admin can edit in a section: the expert's type decides the set, the payload decides availability. */
export function sectionFields(section: DetailsSectionKey, expert: AdminExpertDetail): DetailsFieldDef[] {
  return allSectionFields(section, expert).filter((field) => field.read(expert) !== undefined);
}

function toFormValue(field: DetailsFieldDef, value: string | number | string[] | null | undefined): DetailsFieldValue {
  if (field.kind === 'list' || field.kind === 'idList') return Array.isArray(value) ? [...value] : [];
  return value === null || value === undefined ? '' : String(value);
}

export function sectionInitialValues(
  fields: DetailsFieldDef[],
  expert: AdminExpertDetail,
): Record<string, DetailsFieldValue> {
  return Object.fromEntries(fields.map((field) => [field.name, toFormValue(field, field.read(expert))]));
}

export function sectionSchema(fields: DetailsFieldDef[]) {
  return z.object(Object.fromEntries(fields.map((field) => [field.name, field.rule])));
}

type ApiValue = string | number | string[] | number[] | null;

function toApiValue(field: DetailsFieldDef, value: unknown): ApiValue {
  if (field.kind === 'list') {
    return Array.isArray(value) ? value.map(String) : [];
  }
  if (field.kind === 'idList') {
    return Array.isArray(value) ? value.map(Number) : [];
  }
  const trimmed = typeof value === 'string' ? value.trim() : value == null ? '' : String(value);
  if (trimmed === '') return null;
  return field.kind === 'number' ? Number(trimmed) : trimmed;
}

/**
 * The endpoint only writes the keys it receives, so send just what changed:
 * untouched required fields never trip validation and nothing is rewritten.
 * Returns null when nothing changed.
 */
export function buildDetailsPayload(
  fields: DetailsFieldDef[],
  initial: Record<string, unknown>,
  values: Record<string, unknown>,
): UpdateExpertDetailsRequest | null {
  const payload: Record<string, Record<string, ApiValue>> = {};

  for (const field of fields) {
    const before = toApiValue(field, initial[field.name]);
    const after = toApiValue(field, values[field.name]);
    if (JSON.stringify(before) === JSON.stringify(after)) continue;
    (payload[field.group] ??= {})[field.key] = after;
  }

  return Object.keys(payload).length ? (payload as UpdateExpertDetailsRequest) : null;
}

export interface DetailsErrorMapping {
  fieldErrors: Record<string, string>;
  /** Anything that could not be pinned to a field: domain rules, empty payload, unknown keys. */
  message: string | null;
}

interface ApiErrorLike {
  response?: { status?: number; data?: { message?: string; errors?: Record<string, string[] | string> } };
  message?: string;
}

const FALLBACK_MESSAGE = 'Could not save the changes. Try again.';

export function mapDetailsErrors(fields: DetailsFieldDef[], error: unknown): DetailsErrorMapping {
  const apiError = (error ?? {}) as ApiErrorLike;
  const data = apiError.response?.data;
  const fieldErrors: Record<string, string> = {};
  const unmapped: string[] = [];

  for (const [key, rawMessages] of Object.entries(data?.errors ?? {})) {
    const message = (Array.isArray(rawMessages) ? rawMessages[0] : rawMessages) ?? '';
    const field = fields.find((candidate) => {
      const apiPath = `${candidate.group}.${candidate.key}`;
      return key === apiPath || key.startsWith(`${apiPath}.`);
    });
    if (field) {
      fieldErrors[field.name] ??= message;
    } else if (message) {
      unmapped.push(message);
    }
  }

  if (unmapped.length) {
    return { fieldErrors, message: unmapped.join(' ') };
  }
  if (Object.keys(fieldErrors).length) {
    return { fieldErrors, message: null };
  }
  return { fieldErrors, message: data?.message ?? apiError.message ?? FALLBACK_MESSAGE };
}
